<template>
  <div class="customer-service-page">
    <div class="page-head">
      <div>
        <h2 class="page-title">客服中心</h2>
        <p class="page-subtitle">独立处理商家与艺人的人工客服会话</p>
      </div>
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-label">待接入</div>
          <div class="stat-value">{{ dashboard.waitingCount || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">服务中</div>
          <div class="stat-value">{{ dashboard.servingCount || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">我接待中</div>
          <div class="stat-value">{{ dashboard.myServingCount || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">我的未读</div>
          <div class="stat-value">{{ dashboard.myUnreadCount || 0 }}</div>
        </div>
      </div>
    </div>

    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model.trim="query.keyword"
          clearable
          placeholder="搜索用户/客服/消息"
          style="width: 240px"
          @keyup.enter.native="loadSessions"
        />
        <el-select v-model="query.status" clearable placeholder="会话状态" style="width: 140px">
          <el-option :value="1" label="待接入" />
          <el-option :value="2" label="服务中" />
          <el-option :value="3" label="已关闭" />
        </el-select>
        <el-select v-if="!isCustomerServiceAdmin" v-model="query.adminUserId" clearable placeholder="归属" style="width: 160px">
          <el-option :value="null" label="全部会话" />
        </el-select>
        <el-button type="primary" @click="loadSessions">查询</el-button>
      </div>
      <div class="toolbar-right">
        <el-button size="mini" icon="el-icon-time" @click="openWorkTimeDialog">工作时间</el-button>
        <el-tag v-if="workTime.displayText" :type="workTime.working ? 'success' : 'warning'">
          {{ workTime.enabled ? workTime.displayText : '全天可发起' }}
        </el-tag>
        <el-tag :type="socketConnected ? 'success' : 'danger'">{{ socketConnected ? '实时已连接' : '实时未连接' }}</el-tag>
      </div>
    </div>

    <div class="layout-grid">
      <div class="session-panel">
        <div
          v-for="item in sessions"
          :key="item.sessionId"
          class="session-card"
          :class="{ active: activeSessionId === item.sessionId }"
          @click="selectSession(item)"
        >
          <div class="session-top">
            <div class="session-name">{{ item.clientName || userTypeText(item.clientType) }}</div>
            <el-tag size="mini" :type="statusTagType(item.status)">{{ statusText(item.status) }}</el-tag>
          </div>
          <div class="session-meta">
            <span>{{ userTypeText(item.clientType) }}</span>
            <span>{{ item.clientUserId || '-' }}</span>
          </div>
          <div class="session-last">{{ item.lastMessage || '暂无消息' }}</div>
          <div class="session-bottom">
            <span>{{ item.lastMessageAt || '-' }}</span>
            <el-badge :hidden="!Number(item.unreadCount)" :value="Number(item.unreadCount) || 0" />
          </div>
        </div>
        <div v-if="!sessions.length" class="empty-panel">暂无客服会话</div>
      </div>

      <div class="chat-panel">
        <template v-if="activeSession">
          <div class="chat-head">
            <div>
              <div class="chat-title">{{ activeSession.clientName || '-' }}</div>
              <div class="chat-desc">
                {{ userTypeText(activeSession.clientType) }} / 会话ID：{{ activeSession.sessionId }}
                <span v-if="activeSession.adminName"> / 当前客服：{{ activeSession.adminName }}</span>
              </div>
            </div>
            <div class="chat-actions">
              <el-button
                v-if="canClose(activeSession)"
                type="danger"
                plain
                size="mini"
                @click="closeSession(activeSession)"
              >
                关闭会话
              </el-button>
            </div>
          </div>

          <div ref="messageList" class="message-list">
            <div
              v-for="item in messages"
              :key="item.id"
              class="message-row"
              :class="{ self: !!item.isSelf }"
            >
              <div class="message-meta">
                <span>{{ senderText(item.senderType) }}</span>
                <span>{{ item.createdAt || '-' }}</span>
              </div>
              <div class="message-bubble">{{ item.content || '' }}</div>
            </div>
            <div v-if="!messages.length" class="empty-panel">暂无聊天消息</div>
          </div>

          <div class="message-compose">
            <el-input
              v-model.trim="draft"
              type="textarea"
              :rows="3"
              maxlength="1000"
              show-word-limit
              :disabled="!canReply(activeSession)"
              placeholder="请输入客服回复内容"
              @keydown.enter.native.exact.prevent="sendMessage"
            />
            <div class="compose-actions">
              <el-button type="primary" :disabled="!canReply(activeSession)" @click="sendMessage">发送</el-button>
            </div>
          </div>
        </template>
        <div v-else class="empty-panel chat-empty">请选择左侧会话</div>
      </div>
    </div>

    <el-dialog
      title="客服工作时间"
      :visible.sync="workTimeDialogVisible"
      width="420px"
      :close-on-click-modal="false"
    >
      <el-form label-width="96px">
        <el-form-item label="启用限制">
          <el-switch
            v-model="workTimeForm.enabled"
            active-text="仅工作时间可发起"
            inactive-text="全天可发起"
          />
        </el-form-item>
        <el-form-item label="开始时间">
          <el-time-select
            v-model="workTimeForm.startTime"
            placeholder="开始时间"
            :picker-options="timePickerOptions"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="结束时间">
          <el-time-select
            v-model="workTimeForm.endTime"
            placeholder="结束时间"
            :picker-options="timePickerOptions"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="workTimeDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="workTimeSaving" @click="saveWorkTime">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { hasPermission, getToken } from '../../../utils/auth'
import { API_BASE_URL } from '../../../utils/request'
import {
  closeCustomerServiceSession,
  getCustomerServiceDashboard,
  getCustomerServiceMessages,
  getCustomerServiceSessions,
  getCustomerServiceWorkTime,
  readCustomerServiceSession,
  sendCustomerServiceMessage,
  saveCustomerServiceWorkTime
} from '../../../api/platformManage'

export default {
  name: 'PlatformCustomerServicePage',
  data() {
    return {
      query: {
        keyword: '',
        status: undefined,
        adminUserId: undefined
      },
      dashboard: {},
      sessions: [],
      activeSessionId: '',
      activeSession: null,
      messages: [],
      draft: '',
      socket: null,
      socketConnected: false,
      socketUrlIndex: 0,
      socketReconnectTimer: null,
      socketManuallyClosed: false,
      workTime: {},
      workTimeDialogVisible: false,
      workTimeSaving: false,
      workTimeForm: {
        enabled: true,
        startTime: '09:00',
        endTime: '18:00'
      },
      timePickerOptions: {
        start: '00:00',
        step: '00:30',
        end: '23:30'
      }
    }
  },
  computed: {
    currentAdminId() {
      return this.$store.state.adminInfo && this.$store.state.adminInfo.userId
    },
    roleCodes() {
      return (this.$store.state.adminInfo && this.$store.state.adminInfo.roleCodes) || []
    },
    adminIdentityText() {
      const info = this.$store.state.adminInfo || {}
      return `${info.username || ''} ${info.realName || ''}`.toLowerCase()
    },
    isCustomerServiceAdmin() {
      if (this.adminIdentityText.includes('kefu') || this.adminIdentityText.includes('客服')) {
        return true
      }
      return this.roleCodes.some(code => {
        const normalized = String(code || '').toLowerCase()
        return normalized.includes('customer')
          || normalized.includes('service')
          || normalized.includes('support')
          || normalized.includes('kefu')
      })
    }
  },
  created() {
    this.loadAll()
    this.loadWorkTime()
  },
  mounted() {
    this.connectSocket()
  },
  beforeDestroy() {
    this.closeSocket()
  },
  methods: {
    hasPermission,
    userTypeText(type) {
      if (Number(type) === 1) return '商家'
      if (Number(type) === 2) return '艺人'
      return '用户'
    },
    senderText(type) {
      if (Number(type) === 1) return '商家'
      if (Number(type) === 2) return '艺人'
      if (Number(type) === 3) return '客服'
      return '未知'
    },
    statusText(status) {
      if (Number(status) === 1) return '待接入'
      if (Number(status) === 2) return '服务中'
      if (Number(status) === 3) return '已关闭'
      return '未知'
    },
    statusTagType(status) {
      if (Number(status) === 1) return 'warning'
      if (Number(status) === 2) return 'success'
      return 'info'
    },
    canTake(session) {
      return false
    },
    canReply(session) {
      return this.isCustomerServiceAdmin
        && session
        && Number(session.status) !== 3
        && Number(session.adminUserId) === Number(this.currentAdminId)
    },
    canClose(session) {
      return this.isCustomerServiceAdmin
        && session
        && Number(session.status) !== 3
        && Number(session.adminUserId) === Number(this.currentAdminId)
    },
    canRead(session) {
      return this.isCustomerServiceAdmin
        && session
        && Number(session.adminUserId) === Number(this.currentAdminId)
    },
    async loadAll() {
      await Promise.all([this.loadDashboard(), this.loadSessions()])
    },
    async loadDashboard() {
      this.dashboard = await getCustomerServiceDashboard().catch(() => ({}))
    },
    async loadWorkTime() {
      this.workTime = await getCustomerServiceWorkTime().catch(() => ({}))
    },
    openWorkTimeDialog() {
      const config = this.workTime || {}
      this.workTimeForm = {
        enabled: config.enabled !== false,
        startTime: config.startTime || '09:00',
        endTime: config.endTime || '18:00'
      }
      this.workTimeDialogVisible = true
    },
    async saveWorkTime() {
      if (!this.workTimeForm.startTime || !this.workTimeForm.endTime) {
        this.$message.warning('请选择完整工作时间')
        return
      }
      if (this.workTimeForm.startTime === this.workTimeForm.endTime) {
        this.$message.warning('开始时间和结束时间不能相同')
        return
      }
      this.workTimeSaving = true
      try {
        await saveCustomerServiceWorkTime(this.workTimeForm)
        this.$message.success('工作时间已保存')
        this.workTimeDialogVisible = false
        await this.loadWorkTime()
      } finally {
        this.workTimeSaving = false
      }
    },
    async loadSessions() {
      const list = await getCustomerServiceSessions(this.query).catch(() => [])
      this.sessions = Array.isArray(list) ? list : []
      if (this.activeSessionId) {
        const next = this.sessions.find(item => String(item.sessionId) === String(this.activeSessionId))
        if (next) {
          this.activeSession = next
        }
      }
    },
    async selectSession(session) {
      this.activeSessionId = session.sessionId
      this.activeSession = session
      await this.loadMessages()
      if (this.canRead(session)) {
        await readCustomerServiceSession(session.sessionId).catch(() => null)
      }
      await Promise.all([this.loadDashboard(), this.loadSessions()])
    },
    async loadMessages() {
      if (!this.activeSessionId) {
        this.messages = []
        return
      }
      const list = await getCustomerServiceMessages(this.activeSessionId).catch(() => [])
      this.messages = Array.isArray(list) ? list : []
      this.scrollMessagesToBottom()
    },
    async closeSession(session) {
      await closeCustomerServiceSession(session.sessionId)
      this.$message.success('会话已关闭')
      this.draft = ''
      await Promise.all([this.loadDashboard(), this.loadSessions()])
      if (String(this.activeSessionId) === String(session.sessionId)) {
        this.activeSession = this.sessions.find(item => String(item.sessionId) === String(session.sessionId)) || null
      }
    },
    async sendMessage() {
      const content = String(this.draft || '').trim()
      if (!content || !this.activeSessionId) {
        return
      }
      const message = await sendCustomerServiceMessage(this.activeSessionId, { content })
      this.draft = ''
      this.upsertMessage(message)
      this.upsertSession({
        ...(this.activeSession || {}),
        sessionId: this.activeSessionId,
        status: 2,
        storageStatus: 3,
        adminUserId: String(this.currentAdminId || ''),
        lastMessage: message.content,
        lastMessageAt: message.createdAt
      })
      this.scrollMessagesToBottom()
    },
    buildSocketUrls(token) {
      const path = `/ws/customer-service/admin?token=${encodeURIComponent(token)}`
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const currentUrl = `${protocol}//${window.location.host}${path}`
      const urls = []
      if (API_BASE_URL && API_BASE_URL !== '/') {
        const socketBaseUrl = API_BASE_URL.replace(/\/+$/, '')
          .replace(/^https:/i, 'wss:')
          .replace(/^http:/i, 'ws:')
        if (/^wss?:\/\//i.test(socketBaseUrl)) {
          urls.push(`${socketBaseUrl}${path}`)
        }
      }
      urls.push(currentUrl)
      return Array.from(new Set(urls))
    },
    connectSocket(urlIndex = this.socketUrlIndex) {
      const token = getToken()
      if (!token) {
        return
      }
      this.closeSocket()
      this.socketManuallyClosed = false
      const urls = this.buildSocketUrls(token)
      this.socketUrlIndex = Math.min(urlIndex, urls.length - 1)
      const url = urls[this.socketUrlIndex]
      try {
        const socket = new window.WebSocket(url)
        this.socket = socket
        socket.onopen = () => {
          if (this.socket !== socket) {
            return
          }
          this.socketConnected = true
          this.clearSocketReconnect()
          this.loadAll()
          if (this.activeSessionId) {
            this.loadMessages()
          }
        }
        socket.onclose = () => {
          if (this.socket !== socket) {
            return
          }
          this.socketConnected = false
          this.socket = null
          this.scheduleSocketReconnect(urls.length > 1 ? (this.socketUrlIndex + 1) % urls.length : this.socketUrlIndex)
        }
        socket.onerror = () => {
          if (this.socket !== socket) {
            return
          }
          this.socketConnected = false
          if (this.socket && this.socket.readyState !== window.WebSocket.CLOSED) {
            this.socket.close()
          } else {
            this.socket = null
            this.scheduleSocketReconnect(urls.length > 1 ? (this.socketUrlIndex + 1) % urls.length : this.socketUrlIndex)
          }
        }
        socket.onmessage = (event) => {
          if (this.socket !== socket) {
            return
          }
          this.handleSocketMessage(event.data)
        }
      } catch (error) {
        this.socketConnected = false
        this.scheduleSocketReconnect(urls.length > 1 ? (this.socketUrlIndex + 1) % urls.length : this.socketUrlIndex)
      }
    },
    closeSocket() {
      this.socketManuallyClosed = true
      this.clearSocketReconnect()
      if (this.socket) {
        this.socket.close()
        this.socket = null
      }
      this.socketConnected = false
    },
    scheduleSocketReconnect(nextUrlIndex = this.socketUrlIndex) {
      if (this.socketManuallyClosed || this.socketReconnectTimer) {
        return
      }
      this.socketUrlIndex = nextUrlIndex
      this.socketReconnectTimer = window.setTimeout(() => {
        this.socketReconnectTimer = null
        if (!this.socketConnected && !this.socketManuallyClosed) {
          this.connectSocket(this.socketUrlIndex)
        }
      }, 3000)
    },
    clearSocketReconnect() {
      if (this.socketReconnectTimer) {
        window.clearTimeout(this.socketReconnectTimer)
        this.socketReconnectTimer = null
      }
    },
    scrollMessagesToBottom() {
      this.$nextTick(() => {
        const list = this.$refs.messageList
        if (list) {
          list.scrollTop = list.scrollHeight
        }
      })
    },
    handleSocketMessage(raw) {
      let payload = {}
      try {
        payload = JSON.parse(raw || '{}')
      } catch (error) {
        return
      }
      const type = payload.type
      if (type === 'queue_update' && payload.dashboard) {
        this.dashboard = payload.dashboard
      }
      if (type === 'session_assigned' && payload.session) {
        this.upsertSession(payload.session)
      }
      if (type === 'session_closed' && payload.sessionId) {
        if (payload.session) {
          this.upsertSession(payload.session)
        } else {
          this.markSessionClosed(payload.sessionId)
        }
      }
      if (type === 'message_new' && payload.message) {
        this.applyIncomingMessage(payload)
      }
      if (type === 'message_read' && payload.sessionId) {
        this.applyReadEvent(payload)
      }
    },
    upsertSession(session) {
      if (!session || !session.sessionId) {
        return
      }
      const index = this.sessions.findIndex(item => String(item.sessionId) === String(session.sessionId))
      if (index >= 0) {
        const merged = {
          ...this.sessions[index],
          ...session
        }
        this.$set(this.sessions, index, merged)
        if (String(this.activeSessionId) === String(merged.sessionId)) {
          this.activeSession = merged
        }
        return
      }
      this.sessions = [session].concat(this.sessions)
    },
    markSessionClosed(sessionId) {
      const patch = {
        sessionId,
        status: 3
      }
      this.upsertSession(patch)
      if (String(this.activeSessionId) === String(sessionId)) {
        this.activeSession = {
          ...(this.activeSession || {}),
          ...patch
        }
      }
    },
    normalizeMessage(message) {
      const normalized = {
        ...(message || {})
      }
      normalized.isSelf = Number(normalized.senderType) === 3
      return normalized
    },
    upsertMessage(message) {
      if (!message || !message.id) {
        return
      }
      const normalized = this.normalizeMessage(message)
      const index = this.messages.findIndex(item => String(item.id) === String(normalized.id))
      if (index >= 0) {
        this.$set(this.messages, index, {
          ...this.messages[index],
          ...normalized
        })
        this.scrollMessagesToBottom()
        return
      }
      this.messages = this.messages.concat(normalized)
      this.scrollMessagesToBottom()
    },
    applyIncomingMessage(payload) {
      const message = payload.message
      const sessionId = String(payload.sessionId || message.sessionId || '')
      const existed = this.sessions.some(item => String(item.sessionId) === sessionId)
      if (payload.session) {
        this.upsertSession(payload.session)
      } else {
        const sessionPatch = {
          sessionId,
          lastMessage: message.content,
          lastMessageAt: message.createdAt
        }
        if (Number(message.senderType) !== 3) {
          sessionPatch.unreadCount = Number((this.sessions.find(item => String(item.sessionId) === sessionId) || {}).unreadCount || 0) + 1
        }
        this.upsertSession(sessionPatch)
      }
      if (!existed) {
        this.loadSessions()
      }
      if (this.activeSessionId && sessionId === String(this.activeSessionId)) {
        this.upsertMessage(message)
        if (this.canRead(this.activeSession)) {
          readCustomerServiceSession(this.activeSessionId).then(() => {
            this.upsertSession({
              sessionId,
              unreadCount: 0
            })
          }).catch(() => null)
        }
      }
    },
    applyReadEvent(payload) {
      const sessionId = String(payload.sessionId)
      if (Number(payload.readerType) === 3) {
        this.upsertSession({
          sessionId,
          unreadCount: 0
        })
      }
      if (this.activeSessionId && sessionId === String(this.activeSessionId)) {
        this.messages = this.messages.map(item => {
          if (Number(payload.readerType) === 3 && Number(item.senderType) !== 3) {
            return {
              ...item,
              isRead: 1
            }
          }
          if (Number(payload.readerType) !== 3 && Number(item.senderType) === 3) {
            return {
              ...item,
              isRead: 1
            }
          }
          return item
        })
        this.scrollMessagesToBottom()
      }
    }
  }
}
</script>

<style scoped>
.customer-service-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: calc(100vh - 96px);
  min-height: 0;
  overflow: hidden;
}

.page-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.page-title {
  margin: 0 0 8px;
}

.page-subtitle {
  margin: 0;
  color: #909399;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(110px, 1fr));
  gap: 12px;
}

.stat-card {
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 14px;
  text-align: center;
}

.stat-label {
  color: #909399;
  font-size: 12px;
}

.stat-value {
  margin-top: 8px;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.toolbar-left {
  display: flex;
  gap: 12px;
  align-items: center;
}

.toolbar-right {
  display: flex;
  gap: 10px;
  align-items: center;
}

.layout-grid {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.session-panel,
.chat-panel {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebeef5;
  min-height: 0;
  overflow: hidden;
}

.session-panel {
  padding: 12px;
  overflow-y: auto;
}

.session-card {
  padding: 14px;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.session-card + .session-card {
  margin-top: 10px;
}

.session-card.active {
  border-color: #409eff;
  background: #f5faff;
}

.session-top,
.session-bottom {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
}

.session-name {
  font-weight: 600;
  color: #303133;
}

.session-meta,
.session-bottom,
.chat-desc,
.message-meta {
  color: #909399;
  font-size: 12px;
}

.session-meta,
.message-meta {
  margin-top: 8px;
  display: flex;
  gap: 12px;
}

.session-last {
  margin-top: 10px;
  color: #606266;
  min-height: 20px;
  line-height: 1.5;
  word-break: break-word;
}

.chat-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.chat-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid #ebeef5;
  flex-shrink: 0;
}

.chat-title {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
}

.chat-actions {
  display: flex;
  gap: 10px;
}

.message-list {
  flex: 1;
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
  background: #f7f9fc;
}

.message-row {
  margin-bottom: 16px;
  max-width: 72%;
}

.message-row.self {
  margin-left: auto;
}

.message-bubble {
  margin-top: 6px;
  padding: 12px 14px;
  background: #fff;
  border-radius: 12px;
  color: #303133;
  line-height: 1.7;
  word-break: break-word;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
}

.message-row.self .message-bubble {
  background: #409eff;
  color: #fff;
}

.message-compose {
  padding: 16px 20px 20px;
  border-top: 1px solid #ebeef5;
  flex-shrink: 0;
}

.compose-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.empty-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  color: #909399;
}

.chat-empty {
  min-height: 100%;
}
</style>
